import { Directive, ElementRef, Input, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { fromEvent } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { ImageModel } from 'library-explorer';
import { ModalImageDialogComponent } from '../shared/components/modal-image-dialog/modal-image-dialog.component';

@Directive({
    selector: '[modalImage]',
    standalone: false
})
export class ModalImageDirective implements OnInit {
  @Input() public modalImage: ImageModel;
  @Input() public mobileOnly = true;

  private readonly doubleTapDelay = 300;
  private readonly doubleTapMaxOffset = 24;
  private scaling = false;
  private lastTapTimestamp = 0;
  private lastTapPosition: { x: number, y: number } | null = null;
  private dialogRef: MatDialogRef<any>;

  constructor(
    private readonly dialog: MatDialog,
    private readonly elementRef: ElementRef) {
  }

  ngOnInit(): void {
    const isPhone = window.matchMedia('(max-width: 768px)').matches;

    if (!isPhone && this.mobileOnly) {
      return;
    }

    this.addEventListeners();
  }

  private openModal(): void {
    if (this.dialogRef) {
      return;
    }

    if (!this.modalImage?.uri && !this.modalImage?.url) {
      return;
    }

    this.dialogRef = this.dialog.open(ModalImageDialogComponent, {
      panelClass: ['dialog-no-padding', 'dialog-overflow-visible'],
      backdropClass: ['custom-overlay-backdrop'],
      maxWidth: '100vw',
      data: {
        image: this.modalImage
      }
    });

    this.dialogRef.afterClosed()
      .subscribe(() => this.dialogRef = null);
  }

  private addEventListeners(): void {
    fromEvent(this.elementRef.nativeElement, 'touchstart')
      .pipe(
        filter((event: TouchEvent) => event.touches.length === 2),
        tap((event: TouchEvent) => event?.preventDefault())
      )
      .subscribe(() => this.scaling = true);

    fromEvent(this.elementRef.nativeElement, 'touchmove')
      .pipe(
        filter(() => this.scaling === true),
        tap((event: TouchEvent) => event?.preventDefault())
      )
      .subscribe(() => this.openModal());

    fromEvent(this.elementRef.nativeElement, 'touchend')
      .subscribe((event: TouchEvent) => this.handleTouchEnd(event));

    fromEvent(this.elementRef.nativeElement, 'dblclick')
      .subscribe(() => this.openModal());
  }

  private handleTouchEnd(event: TouchEvent): void {
    const wasScaling = this.scaling;
    this.scaling = false;

    if (wasScaling || event.changedTouches.length !== 1) {
      return;
    }

    const touch = event.changedTouches[0];
    const currentTapTimestamp = Date.now();
    const isDoubleTap =
      currentTapTimestamp - this.lastTapTimestamp <= this.doubleTapDelay
      && this.lastTapPosition
      && Math.abs(touch.clientX - this.lastTapPosition.x) <= this.doubleTapMaxOffset
      && Math.abs(touch.clientY - this.lastTapPosition.y) <= this.doubleTapMaxOffset;

    this.lastTapTimestamp = currentTapTimestamp;
    this.lastTapPosition = { x: touch.clientX, y: touch.clientY };

    if (!isDoubleTap) {
      return;
    }

    event.preventDefault();
    this.lastTapTimestamp = 0;
    this.lastTapPosition = null;
    this.openModal();
  }

}
